// Client for ordering a new MVR through IGIGDataFabricAPI (via the AI API).
// Ordering spends the agency's credits, so the flow is always
// quote fee -> confirm -> place -> poll status.

export const MVR_FLOW_ACTIVITY_HISTORY = 'ActivityHistory';
export const MVR_FLOW_TRANSACTIONAL = 'TransactionalMVR';

export type MvrFlow = typeof MVR_FLOW_ACTIVITY_HISTORY | typeof MVR_FLOW_TRANSACTIONAL;

export interface MvrOrderAddress {
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

export interface MvrOrderInput {
  quoteId: string;
  flow: MvrFlow;
  firstName: string;
  lastName: string;
  middleName?: string;
  licenseNumber: string;
  licenseState: string;
  birthDate?: string;      // YYYY-MM-DD
  gender?: string;
  address?: MvrOrderAddress;
  searchStartDate?: string; // YYYY-MM-DD
  recordType?: string;      // transactional record type; also picks the state fee
  clientName?: string;
}

export interface MvrOrderFee {
  flow: string;
  licenseState: string;
  recordType?: string | null;
  /** Record types this state offers - populates the transactional dropdown. */
  recordTypes: string[];
  /** Start of the record search window - server-derived, shown read-only. */
  searchStartDate: string;
  stateFee: number;
  serviceFee: number;
  totalFee: number;
}

export interface MvrOrderResult {
  quoteId: string;
  flow: string;
  ordered: boolean;
  orderId?: string | null;
  code?: string | null;
  /** DataFabric's reason when it declines — e.g. not enough credits. */
  message?: string | null;
  apiCost: number;
}

export interface MvrOrderStatus {
  quoteId: string;
  flow: string;
  orderId?: string | null;
  orderStatus?: string | null;
  orderDateTime?: string | null;
  reportId?: string | null;
  isComplete: boolean;
}

function apiBase(): string {
  return process.env.REACT_APP_TRUCKING_PORTAL_AI_API_Url ?? '';
}

// The NowCerts token is swapped downstream for a toolbox token, which is what
// tells DataFabric whose credits to charge — as the Hub and MyAMP do.
function authHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}`,
    'X-NC-Token': localStorage.getItem('NCToken') || '',
  };
}

/** Extract the real error message from a FastAPI response body (detail), with fallbacks. */
async function errorMessage(res: Response): Promise<string> {
  try {
    const data = await res.json();
    const detail = data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail)) {
      const msg = detail.map((d: any) => d?.msg || (typeof d === 'string' ? d : '')).filter(Boolean).join('; ');
      if (msg) return msg;
    }
    if (typeof data?.message === 'string' && data.message.trim()) return data.message;
  } catch {
    // body wasn't JSON — fall through
  }
  return `Request failed (${res.status}${res.statusText ? ' ' + res.statusText : ''}).`;
}

/** Quote the cost before ordering. Spends nothing. */
export async function getMvrOrderFee(
  flow: MvrFlow, licenseState: string, recordType?: string,
): Promise<MvrOrderFee> {
  const res = await fetch(`${apiBase()}/ai/mvr/order/fee`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ flow, licenseState, recordType }),
  });
  if (!res.ok) throw new Error(await errorMessage(res));
  return res.json();
}

/** Place the order. Charges the agency's credits. */
export async function placeMvrOrder(input: MvrOrderInput): Promise<MvrOrderResult> {
  const res = await fetch(`${apiBase()}/ai/mvr/order`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(await errorMessage(res));
  return res.json();
}

export async function getMvrOrderStatus(
  quoteId: string, flow: MvrFlow, orderId: string, transactionId: string,
): Promise<MvrOrderStatus> {
  const params = new URLSearchParams({ quoteId, flow, orderId, transactionId });
  const res = await fetch(`${apiBase()}/ai/mvr/order/status?${params}`, {
    method: 'GET',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await errorMessage(res));
  return res.json();
}
